import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, ClipboardList, Sparkles, Heart, MessageCircle, Star } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../i18n/LanguageContext';

export default function HowItWorks() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const steps = [
    {
      icon: UserPlus,
      title: "1. Créez votre compte",
      description: "Inscrivez-vous gratuitement en quelques minutes. Renseignez vos informations de base : nom, âge, localisation et email. Les 500 premiers inscrits bénéficient d'un accès totalement gratuit !",
      color: "from-pink-500 to-rose-500"
    },
    {
      icon: ClipboardList,
      title: "2. Complétez le questionnaire",
      description: "Répondez à notre questionnaire de personnalité approfondi. L'IA Claude analyse vos réponses sur 8 dimensions : valeurs, communication, objectifs de vie, loisirs, vision relationnelle, gestion des conflits, expression émotionnelle et attentes. Plus vous êtes sincère, meilleurs seront vos matchs !",
      color: "from-purple-500 to-indigo-500"
    },
    {
      icon: Sparkles,
      title: "3. L'IA trouve vos matchs",
      description: "Notre intelligence artificielle Claude analyse votre profil et le compare avec des milliers d'autres utilisateurs. Elle calcule un score d'affinité basé sur la compatibilité de vos personnalités, valeurs et objectifs. Les profils les plus compatibles vous sont présentés en premier.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: Heart,
      title: "4. Swipez et matchez",
      description: "Découvrez les profils suggérés avec leur score de compatibilité. Swipez à droite (❤️) si la personne vous intéresse, à gauche (✖️) sinon. Quand deux personnes se likent mutuellement, c'est un match ! Vous recevez une notification instantanée.",
      color: "from-pink-500 to-red-500"
    },
    {
      icon: MessageCircle,
      title: "5. Discutez avec l'aide de l'IA",
      description: "Une fois matché, l'IA vous suggère des débuts de conversation personnalisés basés sur vos points communs. Échangez librement dans notre chat sécurisé. L'IA peut aussi vous aider à relancer une conversation qui stagne.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Star,
      title: "6. Notez et progressez",
      description: "Après avoir échangé, notez votre match sur 5 critères (Confiance, Sérieux, Générosité, Comportement, Communication). Ces notes anonymes aident toute la communauté à identifier les profils de qualité. Consultez vos propres évaluations dans votre Dashboard !",
      color: "from-yellow-500 to-orange-500"
    }
  ];

  const features = [
    {
      title: "Matching Intelligent",
      description: "L'IA analyse plus de 50 points de données pour trouver vos meilleures compatibilités"
    },
    {
      title: "Questionnaire Approfondi",
      description: "8 dimensions de personnalité pour des matchs vraiment compatibles"
    },
    {
      title: "Chat Assisté",
      description: "Suggestions de conversation personnalisées par l'IA"
    },
    {
      title: "Notation Sociale",
      description: "Système de réputation pour une communauté de qualité"
    },
    {
      title: "Multilingue",
      description: "Disponible en français, anglais, espagnol et allemand"
    },
    {
      title: "Sécurisé",
      description: "Données protégées, conforme RGPD, vérification des localisations"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Comment ça marche ? | A-More AI - Guide Complet</title>
        <meta name="description" content="Découvrez comment A-More AI utilise l'intelligence artificielle pour vous trouver l'amour. Guide complet du matching IA, du questionnaire de personnalité à votre premier rendez-vous." />
        <meta name="keywords" content="comment fonctionne A-More AI, guide dating IA, matching intelligent, questionnaire personnalité, rencontre IA" />
        <link rel="canonical" href={`${window.location.origin}/${language}/how-it-works`} />
      </Helmet>

      <div className="min-h-screen py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>

          {/* Hero Section */}
          <div className="glass-card p-8 mb-8">
            <h1 className="text-4xl font-bold gradient-text mb-4">Comment ça marche ?</h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              A-More AI révolutionne les rencontres en ligne grâce à l'intelligence artificielle. 
              Découvrez comment nous utilisons Claude AI pour analyser votre personnalité et vous 
              mettre en relation avec des personnes vraiment compatibles.
            </p>
          </div>

          {/* Steps Section */}
          <div className="space-y-6 mb-12">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="glass-card p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className={`p-4 rounded-xl bg-gradient-to-br ${step.color} flex-shrink-0`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Features Grid */}
          <div className="glass-card p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Nos Fonctionnalités Clés</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="p-4 bg-gradient-to-br from-pink-50 to-orange-50 rounded-xl">
                  <h3 className="font-bold text-gray-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="glass-card p-8 text-center bg-gradient-to-r from-pink-500 to-orange-400">
            <h2 className="text-3xl font-bold text-white mb-4">Prêt à trouver l'amour ?</h2>
            <p className="text-white text-lg mb-6 opacity-90">
              Rejoignez des milliers de célibataires qui ont déjà trouvé leur match parfait grâce à l'IA
            </p>
            <button
              onClick={() => navigate('/auth?mode=signup')}
              className="px-8 py-4 bg-white text-pink-600 rounded-xl font-bold text-lg hover:shadow-2xl transition-shadow"
            >
              Commencer Gratuitement
            </button>
            <p className="text-white text-sm mt-4 opacity-75">
              🎉 Les 500 premiers inscrits sont gratuits !
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
